import { FC, useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Popover, Typography } from '@mui/material';
import { Feature, MapBrowserEvent } from 'ol';
import { Point } from 'ol/geom';
import { useGeographic } from 'ol/proj';
import { OSM } from 'ol/source';
import VectorSource from 'ol/source/Vector';
import { Icon, Style } from 'ol/style';
import {
    AttributionControl,
    DragRotateAndZoomInteraction,
    Map,
    PointerInteraction,
    TileLayer,
    VectorLayer,
    View,
} from 'react-openlayers';

import 'react-openlayers/dist/index.css';

export type GeoMapViewProps = {
    address: string;
    latitude: number;
    longitude: number;
};

const GeoMapView: FC<GeoMapViewProps> = ({ address, latitude, longitude }) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const [anchorPosition, setAnchorPosition] = useState<{
        vertical: number;
        horizontal: number;
    }>({ vertical: 0, horizontal: 0 });

    const [open, setOpen] = useState(false);

    const coordinates = [longitude, latitude];
    const feature = new Feature(new Point(coordinates));

    useEffect(() => {
        // this method is used to set the projection to geographic. It is not a React hook, but a method from OpenLayers.
        // It is necessary to call it once to ensure the map uses the correct projection.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useGeographic();
    }, []);

    const markerStyle = new Style({
        image: new Icon({
            anchor: [0.5, 1],
            src: '/images/marker.png', // Ensure this path is correct relative to the public folder
            width: 32,
            height: 32,
        }),
    });

    const source = new VectorSource({
        features: [feature],
    });

    const handleMapClick = (event: MapBrowserEvent) => {
        const clickedFeature = event.map.forEachFeatureAtPixel(event.pixel, (feature) => {
            return feature;
        });

        if (clickedFeature) {
            setOpen(true);
            setAnchorEl(event.originalEvent.currentTarget as HTMLButtonElement);
            setAnchorPosition({ horizontal: event.pixel[0], vertical: event.pixel[1] });
        } else {
            setOpen(false);
            setAnchorEl(null);
            setAnchorPosition({ horizontal: 0, vertical: 0 });
        }
    };

    const handleClose = () => {
        setOpen(false);
        setAnchorEl(null);
    };

    return (
        <Map style={{ height: 450, width: '100%' }}>
            <TileLayer source={new OSM()} />
            <View center={coordinates} zoom={18} />
            <DragRotateAndZoomInteraction />
            <AttributionControl />
            <PointerInteraction handleDragEvent handleDownEvent={handleMapClick} />
            <VectorLayer source={source} style={markerStyle} />
            <Popover
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                onClick={handleClose}
                anchorReference={'anchorEl'}
                anchorOrigin={anchorPosition}
            >
                <Typography sx={{ padding: 2 }}>
                    {address}{' '}
                    <IconButton>
                        <CloseIcon />
                    </IconButton>
                </Typography>
            </Popover>
        </Map>
    );
};

export default GeoMapView;
